import { ActionIcon, Button, Card, Grid, Group, Stack, Text } from '@mantine/core';
import { IconEye, IconLayoutGrid, IconList, IconPlus, IconSquareArrowRight, IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import type { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { setUseCaseGridView } from '../../../../persisted/global/globalActions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultUseCaseResource,
    type UseCaseResource,
    useUseCaseActions,
} from '../../../../resource/resources/artifacts/use_case/useCaseResource';
import {
    defaultUseCaseStandardAssociationResource,
    useUseCaseStandardAssociationActions,
} from '../../../../resource/resources/artifacts/use_case/useCaseStandardAssociationResource';
import { useUserActions } from '../../../../resource/resources/persistables/user/userResource';
import type { RootState } from '../../../../store/rootReducer';
import { useUserId } from '../../../authentication/useUserId';
import { ConditionalComponent } from '../../../reusable-components/ConditionalComponent';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { defaultLoadingNode } from '../../../util/parseTreeFunctions';

type UseCasesStructureComponentTreeNode = SerializableRenderTreeNode<UseCaseResource>;

const parseTree = (
    resources: QueryObserverResult<Array<UseCaseResource> | null>
): Array<UseCasesStructureComponentTreeNode> => {
    if (resources.isLoading) {
        return [defaultLoadingNode];
    }
    if (!resources.data) {
        return [];
    }

    return resources.data
        .map((resource) => ({
            key: resource.id,
            label: resource.name,
            data: resource,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));
};

export const UseCasesStructureComponent = ({
    projectVersionId,
    standardObjectId,
    onSelected,
    onOpen,
}: {
    projectVersionId: string | undefined;
    standardObjectId: string | undefined;
    onSelected: (id: string) => void;
    onOpen: (id: string) => void;
}) => {
    const { showContextMenu } = useContextMenu();
    const { id: projectId } = useParams<{ id: string }>();
    const userId = useUserId('');
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const dispatch = useDispatch();
    const viewMode = useSelector((state: RootState) => (state.persisted.global.useCaseGridView ? 'grid' : 'list'));
    const [selectedNode, setSelectedNode] = React.useState<UseCasesStructureComponentTreeNode | null>(null);
    const [nodes, setNodes] = React.useState<Array<UseCasesStructureComponentTreeNode>>([]);

    const useCaseActions = useUseCaseActions();
    const useCaseStandardAssociationActions = useUseCaseStandardAssociationActions();
    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const useCasesQuery = useCaseActions.useGetAllUseCasesByStandardAssociationAndProjectVersion(
        projectVersionId,
        standardObjectId
    );
    const allUseCasesQuery = useCaseActions.useGetAllResources(projectVersionId);

    const addUseCase = useCaseActions.useAddResource();
    const addUseCaseStandardAssociation = useCaseStandardAssociationActions.useAddResource();
    const deleteUseCaseStandardAssociation = useCaseStandardAssociationActions.useDeleteResourceByUseCaseAndStandard();

    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;

    React.useEffect(() => {
        const newNodes = parseTree(useCasesQuery);
        setNodes(newNodes);
    }, [useCasesQuery.data, projectVersionId]);

    React.useEffect(() => {
        setSelectedNode(null);
    }, [projectVersionId, standardObjectId]);

    const nodeSelected = (node: UseCasesStructureComponentTreeNode) => {
        setSelectedNode(node);
        onSelected(node.key);
    };

    return (
        <Card shadow={'sm'} padding={'md'} radius={'md'} withBorder style={{ height: '100%' }}>
            <ConditionalComponent
                condition={Boolean(standardObjectId && projectVersionId)}
                label={'Select a Standard to continue'}
            >
                <Stack h={'100%'}>
                    <ResourceValidationTitleBar
                        resourceType={ResourceType.STANDARD}
                        customButtons={
                            <Group>
                                <ActionIcon
                                    size={'sm'}
                                    onClick={(event) => {
                                        showContextMenu([
                                            {
                                                key: 'list',
                                                title: 'List',
                                                icon: <IconList />,
                                                onClick: () => dispatch(setUseCaseGridView(false)),
                                            },
                                            {
                                                key: 'grid',
                                                title: 'Grid',
                                                icon: <IconLayoutGrid />,
                                                onClick: () => dispatch(setUseCaseGridView(true)),
                                            },
                                        ])(event);
                                    }}
                                    variant={'transparent'}
                                    color={'var(--mantine-color-base-button)'}
                                >
                                    <IconEye />
                                </ActionIcon>
                                <Show when={isProcessAdmin}>
                                    <ActionIcon
                                        size={'sm'}
                                        onClick={() =>
                                            OpenModal.SelectionOrCreateModal(
                                                'Select or Create a Use Case',
                                                allUseCasesQuery.data
                                                    ?.filter(
                                                        (useCase) =>
                                                            !useCasesQuery.data?.some(
                                                                (association) =>
                                                                    association.objectId === useCase.objectId
                                                            )
                                                    )
                                                    .map((useCase) => ({ label: useCase.name, value: useCase.id })) ??
                                                    [],
                                                true,
                                                null,
                                                (value) => {
                                                    if (value && standardObjectId) {
                                                        addUseCaseStandardAssociation.mutate({
                                                            resource: {
                                                                ...defaultUseCaseStandardAssociationResource,
                                                                standard: standardObjectId,
                                                            },
                                                            parentId: value,
                                                        });
                                                    }
                                                },
                                                (value, multiLineValue) => {
                                                    if (value && standardObjectId) {
                                                        addUseCase.mutate(
                                                            {
                                                                resource: {
                                                                    ...defaultUseCaseResource,
                                                                    name: value,
                                                                    description: multiLineValue ?? '',
                                                                },
                                                                parentId: projectVersionId,
                                                            },
                                                            {
                                                                onSuccess: (result) => {
                                                                    const useCaseId = result?.content?.id;
                                                                    if (useCaseId) {
                                                                        addUseCaseStandardAssociation.mutate({
                                                                            resource: {
                                                                                ...defaultUseCaseStandardAssociationResource,
                                                                                standard: standardObjectId,
                                                                            },
                                                                            parentId: useCaseId,
                                                                        });
                                                                    }
                                                                },
                                                            }
                                                        );
                                                    }
                                                },
                                                undefined,
                                                'Name',
                                                true,
                                                'Description',
                                                800
                                            )
                                        }
                                        variant={'transparent'}
                                        color={'var(--mantine-color-base-button)'}
                                    >
                                        <IconPlus />
                                    </ActionIcon>
                                </Show>
                            </Group>
                        }
                    >
                        <Text>Use Cases ({nodes.length}) of Standard</Text>
                    </ResourceValidationTitleBar>
                    <Show when={viewMode === 'list'}>
                        <Tree
                            nodes={nodes}
                            rootExpanded
                            selectedNodeKey={selectedNode?.key}
                            onNodeSelected={nodeSelected}
                            getElements={(node) => ({
                                onDoubleClick: () => onOpen(node.data.id),
                                hoverNode: (
                                    <Group gap={0}>
                                        <TreeHoverButton
                                            Icon={IconSquareArrowRight}
                                            onClick={() => onOpen(node.data.id)}
                                        />
                                        {isProcessAdmin && (
                                            <TreeHoverButton
                                                Icon={IconTrash}
                                                onClick={() => {
                                                    if (standardObjectId) {
                                                        deleteResourceDialogFunction.deleteUseCaseStructureAssociationResource(
                                                            node.data.id,
                                                            standardObjectId,
                                                            node.data.name,
                                                            deleteUseCaseStandardAssociation
                                                        );
                                                    }
                                                }}
                                            />
                                        )}
                                    </Group>
                                ),
                            })}
                        />
                    </Show>
                    <Show when={viewMode === 'grid'}>
                        <Grid>
                            {nodes.map((node) => (
                                <Grid.Col key={node.key} span={{ base: 12, md: 6 }}>
                                    <Button
                                        w={'100%'}
                                        size={'lg'}
                                        rightSection={
                                            <Group gap={0}>
                                                <TreeHoverButton
                                                    Icon={IconSquareArrowRight}
                                                    onClick={() => onOpen(node.data.id)}
                                                />
                                                {isProcessAdmin && (
                                                    <TreeHoverButton
                                                        Icon={IconTrash}
                                                        onClick={() => {
                                                            if (standardObjectId) {
                                                                deleteResourceDialogFunction.deleteUseCaseStructureAssociationResource(
                                                                    node.data.id,
                                                                    standardObjectId,
                                                                    node.data.name,
                                                                    deleteUseCaseStandardAssociation
                                                                );
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Group>
                                        }
                                        justify="space-between"
                                        style={
                                            selectedNode?.key === node.key ?
                                                { border: '2px solid var(--mantine-text-secondary-color)' }
                                            :   undefined
                                        }
                                        onClick={() => {
                                            setSelectedNode(node);
                                            onSelected(node.key);
                                        }}
                                    >
                                        {node.label}
                                    </Button>
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Show>
                </Stack>
            </ConditionalComponent>
        </Card>
    );
};
